/**
 * The equipment register — the screen.
 *
 * Every vehicle and item of plant on the airport, the airport's own and each
 * stakeholder's, sorted the way the person reading it needs it: by type, by
 * condition, by where it is, by who drives it, by what expires next, by what
 * inspection is due, or by what has gone longest without being touched.
 *
 * The sort is the feature. A register of thirty items is a list; a register
 * sorted by licence expiry with the undated ones at the top is a morning's
 * work. So every sort puts what is wrong or unrecorded FIRST — see
 * `sortFleet` for why putting undated rows at the bottom hides the worst of
 * them behind the ones the platform can measure.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  answerFleet,
  buildFleetDesk,
  CONDITION_LABEL,
  FLEET_KINDS,
  FLEET_ASSET_TYPES,
  FLEET_MODEL_PROMPT,
  isDown,
  fleetFromAssets,
  KIND_LABEL,
  materialForFleet,
  sortFleet,
  SORTS,
  UNASSIGNED_DEPARTMENT,
  type FleetDesk,
  type FleetRegister,
  type FleetRow,
  type SortKey,
} from "../../domain/fleet";
import { Button, Card, EmptyState, Field, Select } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { AskCard, Refs, Tiles, x as ex } from "../executive/shared";
import { EQUIPMENT_SPEC, EQUIPMENT_TOPIC } from "../../domain/registerSpecs";
import { RegisterNotices } from "../shared/RegisterNotices";
import { useRegisterDoc } from "../shared/useRegisterRows";
import { useScreenContext } from "../../assistant/screen";
import { ChangeLog, ImportDialog, ItemActions, ItemDialog } from "./FleetEditors";
import { t, tn } from "../../i18n";
import s from "./Equipment.module.css";

export const FLEET_TOPIC = EQUIPMENT_TOPIC;

export function FleetPage() {
  const session = useSession();
  /*
   * Entered, not read from the asset registry. Where the pack declares no
   * vehicle type, none of this is a platform asset; the register is kept on
   * the platform row by row (build 5) — one row per item and one per change
   * in its history — so two people editing two items never overwrite each
   * other, and two editing the same item are told. The demo starts from the
   * sample; the platform starts empty.
   */
  const entered = useRegisterDoc(EQUIPMENT_SPEC);
  /*
   * The platform first, the entered copy second.
   *
   * The airports pack now declares vehicle types, so on a deployment running
   * it the register is read from `/v1/assets` with the attributes the pack
   * declares — and the rules engine is watching the same dates, so a permit
   * that lapses raises an alert on the day it lapses rather than waiting for
   * somebody to open this screen.
   *
   * A deployment on an older pack returns nothing, and that emptiness is the
   * signal to fall back. Which of the two is in use is said on the face of
   * the screen: a register read from the platform and one somebody typed are
   * different kinds of evidence and must not look alike.
   */
  const platform = useQuery({
    queryKey: ["fleet-assets", session.site.id],
    staleTime: 60_000,
    queryFn: () => session.source.listAssetsOfTypes(session.site.id, FLEET_ASSET_TYPES),
  });
  /*
   * The open defects, joined on the tag.
   *
   * `/v1/assets` carries the attributes but not the defects, and the defect is
   * what says whether a machine is off the road — and now, with
   * `equipment_awaiting_parts` as its own type, WHICH KIND of off the road.
   * Without this the platform-read register would show every vehicle
   * serviceable, which is the most confident possible way to be wrong.
   */
  const estate = useQuery({
    queryKey: ["fleet-defects", session.site.id],
    staleTime: 60_000,
    queryFn: () => session.source.getEstateSummary(session.site.id, [...FLEET_ASSET_TYPES]),
  });
  const fromPlatform = (platform.data?.length ?? 0) > 0;
  const register = useMemo(() => {
    if (!fromPlatform) return entered.value;
    const byTag = new Map<string, { summary: string; severity: number; typeKey?: string }[]>();
    for (const d of estate.data?.defects.openList ?? []) {
      const list = byTag.get(d.assetTag) ?? [];
      list.push({ summary: d.summary, severity: d.severity, typeKey: d.defectType });
      byTag.set(d.assetTag, list);
    }
    return fleetFromAssets((platform.data ?? []).map((a) => ({ ...a, openDefects: byTag.get(a.tag) ?? [] })));
  }, [fromPlatform, platform.data, estate.data, entered.value]);
  const [sort, setSort] = useState<SortKey>("condition");
  const [kind, setKind] = useState<string>("all");
  const [owner, setOwner] = useState<string>("all");
  const [dept, setDept] = useState<string>("all");
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  /*
   * Only the entered register is edited here. A register read from the
   * platform is changed in the platform's asset registry, where the rules
   * engine watches it; editing a copy of it here would fork the evidence.
   */
  const editable = !fromPlatform;
  const save = (next: FleetRegister, base?: FleetRegister) => entered.commit(next, base);
  /* retired items are kept, with their history, but are off the working register */
  const working = useMemo<FleetRegister>(
    () => ({ ...register, items: register.items.filter((i) => !i.retired) }),
    [register],
  );
  const retired = register.items.filter((i) => i.retired);

  const desk = useMemo<FleetDesk>(
    () =>
      buildFleetDesk({
        now: new Date(),
        siteCode: session.site.code,
        register: working,
      }),
    [working, session.site.code],
  );

  const shown = useMemo(() => {
    const filtered = desk.rows.filter(
      (r) =>
        (kind === "all" || r.item.kind === kind) &&
        (owner === "all" || r.item.owner.name === owner) &&
        (dept === "all" || (r.item.department ?? UNASSIGNED_DEPARTMENT) === dept),
    );
    return sortFleet(filtered, sort);
  }, [desk.rows, kind, owner, dept, sort]);

  useScreenContext({
    route: "/equipment",
    title: "Equipment",
    summary: desk.boardLine,
    material: materialForFleet(desk),
    figures: desk.figures,
    own: (q) => answerFleet(q, desk),
    prompt: FLEET_MODEL_PROMPT,
    offers: [
      { label: "What is expiring?", ask: "what licences or permits are expiring?" },
      { label: "What is off the road?", ask: "what is unserviceable?" },
      { label: "Whose equipment?", ask: "whose equipment is on the airport?" },
    ],
  });

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t("Asset Management") })}
      title="Equipment"
      actions={
        <div className={s.actions}>
          {editable && (
            <>
              <Button variant="primary" onClick={() => setAdding(true)} data-testid="fleet-add">
                {t("Add an item")}
              </Button>
              <Button onClick={() => setImporting(true)} data-testid="fleet-import">
                {t("Import from Excel or CSV")}
              </Button>
            </>
          )}
          <button type="button" className={ex.tag} onClick={() => window.print()}>
            {t("Print")}
          </button>
        </div>
      }
    >
      <p className={s.lede}>
        {t(
          "Every vehicle and machine on the airport, with its owner, driver, permits, inspections and maintenance. Problems sort first.",
        )}
      </p>
      <p className={s.note} data-testid="fleet-basis">
        {fromPlatform
          ? t(
              "From the platform. It alerts you when a license, insurance or airside permit lapses. A missing date shows as unknown.",
            )
          : t(
              "Entered by hand, not from the platform, so there are no automatic expiry alerts. It's saved under the signed-in user.",
            )}
      </p>

      <Tiles items={desk.tiles} currency={"SAR"} />
      {editable && <RegisterNotices doc={entered} what={t("equipment register")} />}

      <Card title="The register" sub="Problems and missing data first">
        <div className={ex.row}>
          <Field label={t("Sort by")}>
            <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label={t("Sort by")}>
              {SORTS.map((o) => (
                <option key={o.key} value={o.key}>
                  {t(o.label)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("Type")}>
            <Select value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t("Type")}>
              <option value="all">{t("Every type")}</option>
              {desk.byKind.map((k) => (
                <option key={k.kind} value={k.kind}>
                  {`${t(k.label)} (${k.count})`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("Department")}>
            <Select value={dept} onChange={(e) => setDept(e.target.value)} aria-label={t("Department")}>
              <option value="all">{t("Every department")}</option>
              {desk.departments.map((d) => (
                <option key={d.name} value={d.name}>
                  {`${d.name} (${d.count})`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("Owner")}>
            <Select value={owner} onChange={(e) => setOwner(e.target.value)} aria-label={t("Owner")}>
              <option value="all">{t("Everybody")}</option>
              {desk.owners.map((o) => (
                <option key={o.name} value={o.name}>
                  {`${o.name} (${o.count})`}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <p className={s.meta}>
          {t("{shown} of {total} shown", { shown: shown.length, total: desk.rows.length })}
          {entered.by ? t(" · entered by {who}", { who: entered.by }) : ""}
        </p>

        {shown.length === 0 ? (
          <EmptyState>{t("Nothing on the register matches this filter.")}</EmptyState>
        ) : (
          <div className={ex.tableWrap}>
            <table className={ex.table} data-testid="fleet-table">
              <thead>
                <tr>
                  <th>{t("Item")}</th>
                  <th>{t("Type")}</th>
                  <th>{t("Owner")}</th>
                  <th>{t("Condition")}</th>
                  <th>{t("Department")}</th>
                  <th>{t("Location")}</th>
                  <th>{t("Driver")}</th>
                  <th>{t("License")}</th>
                  <th>{t("Airside permit")}</th>
                  <th>{t("Safety inspection")}</th>
                  <th>{t("Maintenance")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <Row key={row.item.id} row={row} open={open === row.item.id} onOpen={() => setOpen(row.item.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && (
        <Detail
          row={desk.rows.find((r) => r.item.id === open) ?? null}
          edit={editable ? { register, save } : undefined}
        />
      )}

      {adding && (
        <ItemDialog
          register={register}
          onSave={save}
          onClose={() => setAdding(false)}
          onAdded={(tag) => setOpen(register.items.find((i) => i.tag === tag)?.id ?? null)}
        />
      )}
      {importing && <ImportDialog register={register} onSave={save} onClose={() => setImporting(false)} />}

      {editable && (
        <Card
          title="Change log"
          sub="Who changed what, when, before and after. Read-only."
        >
          <ChangeLog changes={register.changes ?? []} showTag limit={15} />
        </Card>
      )}

      {retired.length > 0 && (
        <Card title="Retired" sub="Kept with their history. Tags are never reused.">
          <div className={ex.tableWrap}>
            <table className={ex.table} data-testid="fleet-retired">
              <tbody>
                {retired.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link to={`/equipment/${encodeURIComponent(i.tag)}`}>
                        <b>{i.tag}</b>
                      </Link>
                      <div className={ex.muted}>{i.name}</div>
                    </td>
                    <td>{i.owner.name}</td>
                    <td>
                      {i.retired!.at.slice(0, 10)} · {i.retired!.by}
                      <div className={ex.muted}>{i.retired!.reason}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AskCard
        title="The line for the board"
        sub="Written from the register and checked against it"
        board={desk.boardLine}
        material={materialForFleet(desk)}
        figures={desk.figures}
        own={(q) => answerFleet(q, desk)}
        prompt={FLEET_MODEL_PROMPT}
        placeholder={t("What is expiring? What is unserviceable? Whose equipment is on the airport?")}
      />

      {desk.gaps.length > 0 && (
        <Card title="What this register does not know" sub="Gaps to keep in mind">
          <ul className={ex.plain}>
            {desk.gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Card>
      )}
    </Page>
  );
}

/** A date cell that says "not recorded" rather than leaving a blank to be read as fine. */
function When({ date, days }: { date?: string; days: number | null }) {
  if (!date) return <span className={ex.tagRed}>{t("not recorded")}</span>;
  const tone = days === null ? ex.tag : days < 0 ? ex.tagRed : days <= 30 ? ex.tag : ex.tagGreen;
  return (
    <>
      <div>{date}</div>
      <span className={tone}>
        {days === null
          ? t("unknown")
          : days < 0
            ? t("{n} overdue", { n: tn(-days, "{n} day", "{n} days") })
            : t("in {n}", { n: tn(days, "{n} day", "{n} days") })}
      </span>
    </>
  );
}

function daysTo(date: string | undefined, now = new Date()): number | null {
  if (!date) return null;
  const at = Date.parse(date.length <= 10 ? `${date}T00:00:00Z` : date);
  return Number.isNaN(at) ? null : Math.floor((at - now.getTime()) / 86_400_000);
}

function Row({ row, open, onOpen }: { row: FleetRow; open: boolean; onOpen: () => void }) {
  const i = row.item;
  const tone = isDown(i.condition) ? ex.tagRed : i.condition === "degraded" ? ex.tag : ex.tagGreen;
  return (
    <tr onClick={onOpen} aria-selected={open} data-testid={`fleet-${i.tag}`} style={{ cursor: "pointer" }}>
      <td>
        <b>{i.tag}</b>
        <div className={ex.muted}>{i.name}</div>
        {(i.make || i.model) && <div className={ex.muted}>{[i.make, i.model, i.year].filter(Boolean).join(" · ")}</div>}
      </td>
      <td>{t(KIND_LABEL[i.kind])}</td>
      <td>
        {i.owner.name}
        {i.owner.kind === "stakeholder" && <div className={ex.muted}>{t("stakeholder")}</div>}
      </td>
      <td>
        <span className={tone}>{t(CONDITION_LABEL[i.condition])}</span>
        {i.conditionNote && <div className={ex.muted}>{i.conditionNote}</div>}
      </td>
      <td>
        {i.department ?? <span className={ex.tagRed}>{t("not recorded")}</span>}
        {i.usedByDepartment && <div className={ex.muted}>{t("used by {dept}", { dept: i.usedByDepartment })}</div>}
      </td>
      <td>{i.location ?? <span className={ex.tagRed}>{t("not recorded")}</span>}</td>
      <td>
        {i.driver ? (
          <>
            <div>{i.driver.name}</div>
            {i.driver.permitExpiry ? (
              <span className={(daysTo(i.driver.permitExpiry) ?? 0) < 0 ? ex.tagRed : ex.muted}>
                {t("permit {date}", { date: i.driver.permitExpiry })}
              </span>
            ) : (
              <span className={ex.tagRed}>{t("no permit date")}</span>
            )}
          </>
        ) : (
          <span className={ex.tagRed}>{t("none assigned")}</span>
        )}
      </td>
      <td>
        <When date={i.licenceExpiry} days={daysTo(i.licenceExpiry)} />
      </td>
      <td>
        <When date={i.airsidePermit?.expiry} days={daysTo(i.airsidePermit?.expiry)} />
      </td>
      <td>
        <When date={i.safetyInspection?.nextDueAt} days={daysTo(i.safetyInspection?.nextDueAt)} />
      </td>
      <td>
        {row.lastMaintenanceAt ? (
          <>
            <div>{row.lastMaintenanceAt}</div>
            <span className={ex.muted}>{tn(row.maintenanceCount, "{n} entry", "{n} entries")}</span>
          </>
        ) : (
          <span className={ex.tagRed}>{t("nothing recorded")}</span>
        )}
      </td>
    </tr>
  );
}

function Detail({
  row,
  edit,
}: {
  row: FleetRow | null;
  /** present when the register may be changed from this screen */
  edit?: { register: FleetRegister; save: (next: FleetRegister, base?: FleetRegister) => Promise<void> };
}) {
  const session = useSession();
  const qc = useQueryClient();
  const [raising, setRaising] = useState(false);
  const [raised, setRaised] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  if (!row) return null;
  const i = row.item;

  /*
   * The work order is raised against the asset TAG, which is what makes it
   * findable from the other end: the same job appears on the work-orders
   * board, in the asset's own history, and against the defect it answers.
   * Raising it here and writing the reason into a note instead would have
   * produced a maintenance record only this screen could see.
   */
  const raise = async () => {
    setRaising(true);
    setFailed(null);
    const worst = row.flags[0];
    const why = worst ? `${t(worst.what)}` : t(CONDITION_LABEL[i.condition]);
    try {
      const wo = await session.source.createWorkOrder({
        siteId: session.site.id,
        title: `${i.tag} ${i.name} — ${why}`,
        kind: isDown(i.condition) ? "corrective" : "preventive",
        priority: i.condition === "unserviceable" ? 1 : isDown(i.condition) ? 2 : 3,
        assetTag: i.tag,
        description: [
          i.department ? t("Department: {dept}", { dept: i.department }) : "",
          i.driver ? t("Driver assigned: {who}", { who: i.driver.name }) : "",
          ...row.flags.map((f) => `• ${t(f.what)}`),
        ]
          .filter(Boolean)
          .join("\n"),
      });
      setRaised(wo.number ?? wo.id);
      /* the history page reads its own copy of the list; without this it kept
         showing the list from before the raise for up to half a minute */
      void qc.invalidateQueries({ queryKey: ["fleet-history-wo", session.site.id] });
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setRaising(false);
    }
  };

  return (
    <Card
      title={`${i.tag} — ${i.name}`}
      sub={t("{owner} · {kind}", { owner: i.owner.name, kind: t(KIND_LABEL[i.kind]) })}
    >
      <div className={ex.row} data-testid={`fleet-actions-${i.tag}`}>
        <Button variant="primary" onClick={() => void raise()} disabled={raising}>
          {raising ? t("Creating…") : t("New Work Order")}
        </Button>
        <Link className={ex.tag} to={`/equipment/${encodeURIComponent(i.tag)}`}>
          {t("History and notes")}
        </Link>
        {edit && <ItemActions item={i} register={edit.register} onSave={edit.save} />}
      </div>
      {raised && (
        <p className={ex.saved} data-testid={`fleet-raised-${i.tag}`}>
          {t("Work order {number} raised against {tag}.", { number: raised, tag: i.tag })}{" "}
          <Link to="/work-orders">{t("Open the work-orders board")}</Link>
        </p>
      )}
      {failed && <p className={ex.muted}>{t("The work order was not raised: {why}", { why: failed })}</p>}
      <div className={ex.two}>
        <div>
          <div className={ex.colTitle}>{t("What stops it today")}</div>
          {row.flags.length === 0 ? (
            <p className={ex.cardText}>{t("No issues: every date is recorded and current.")}</p>
          ) : (
            <ul className={ex.plain} data-testid={`fleet-flags-${i.tag}`}>
              {row.flags.map((f) => (
                <li key={f.what}>
                  <span className={f.kind === "expiring" ? ex.tag : ex.tagRed}>{t(f.kind)}</span> {t(f.what)}
                  {f.days !== null && f.days < 0 && ` — ${t("{n} ago", { n: tn(-f.days, "{n} day", "{n} days") })}`}
                  {f.days !== null && f.days >= 0 && ` — ${t("in {n}", { n: tn(f.days, "{n} day", "{n} days") })}`}
                </li>
              ))}
            </ul>
          )}
          <div className={ex.colTitle}>{t("On record")}</div>
          <dl className={ex.kv}>
            <div>
              <dt>{t("Registration")}</dt>
              <dd>{i.registration ?? t("not recorded")}</dd>
            </div>
            <div>
              <dt>{t("Airside permit")}</dt>
              <dd>{i.airsidePermit?.ref ?? t("not recorded")}</dd>
            </div>
            <div>
              <dt>{t("Hours run")}</dt>
              <dd>{i.hoursRun != null ? i.hoursRun.toLocaleString() : t("not recorded")}</dd>
            </div>
            <div>
              <dt>{t("Last inspection")}</dt>
              <dd>{i.safetyInspection?.lastAt ?? t("not recorded")}</dd>
            </div>
            <div>
              <dt>{t("Inspection result")}</dt>
              <dd>{i.safetyInspection?.result ?? t("not recorded")}</dd>
            </div>
          </dl>
        </div>
        <div>
          <div className={ex.colTitle}>{t("Maintenance log")}</div>
          {(i.maintenance ?? []).length === 0 ? (
            <p className={ex.cardText}>{t("Nothing recorded against it.")}</p>
          ) : (
            <div className={ex.tableWrap}>
              <table className={ex.table} data-testid={`fleet-log-${i.tag}`}>
                <tbody>
                  {[...(i.maintenance ?? [])]
                    .sort((a, b) => b.at.localeCompare(a.at))
                    .map((m) => (
                      <tr key={`${m.at}-${m.what}`}>
                        <td>{m.at}</td>
                        <td>
                          <b>{m.what}</b>
                          <div className={ex.muted}>{[t(m.kind), m.by, m.workOrder].filter(Boolean).join(" · ")}</div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          <Refs list={[{ kind: "entered", ref: "equipment register", label: t("the equipment register") }]} wide />
        </div>
      </div>
    </Card>
  );
}

/** The types this register groups equipment into, for anyone extending it. */
export const FLEET_KIND_LIST = FLEET_KINDS;
